import { Button, Grid, GridColumn, IFormHelperProps, List, ListItem, Notice, PassField, Separator, Text, TextField } from '@teliaee/design-system';
import { observer } from 'mobx-react';
import * as React from 'react';
import { FormEvent } from 'react';
import LoginButton from '../components/LoginButton';
import { DealerLoginCredentials } from '../stores/DealerLoginStore';
import useStores from '../stores/UseStores';
import { PW_FIELD_ID, USERNAME_FIELD_ID, getHelperErrorMessage, helperInErrorState, notEmptyFieldHelper } from '../util/FieldHelperUtil';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';

interface DealerLoginFormProps {
  handleSubmit: () => void;
  isSubmitting: boolean;
  credentials: DealerLoginCredentials;
}

const DealerLoginForm = observer((props: DealerLoginFormProps): React.JSX.Element => {
  const { isSubmitting, credentials, handleSubmit } = props;
  const {
    translateStore: { translate },
    uiStore: { getLoginPanelGridWidth, setSelectedTab },
  } = useStores();
  const [usernameDirty, setUsernameDirty] = React.useState<boolean>(false);
  const [passwordDirty, setPasswordDirty] = React.useState<boolean>(false);
  const [isUsernameInvalid, setIsUsernameInvalid] = React.useState<boolean>(false);
  const [isPasswordInvalid, setIsPasswordInvalid] = React.useState<boolean>(false);
  const [showErrorNotice, setShowErrorNotice] = React.useState<boolean>(false);

  const [usernameHelper, setUsernameHelper] = React.useState<IFormHelperProps | undefined>(undefined);
  const [passwordHelper, setPasswordHelper] = React.useState<IFormHelperProps | undefined>(undefined);

  React.useEffect(() => {
    const helper = notEmptyFieldHelper(translate, credentials.username, usernameDirty);

    setUsernameHelper(helper);
    setIsUsernameInvalid(helperInErrorState(helper));
  }, [credentials.username, usernameDirty]);

  React.useEffect(() => {
    const helper = notEmptyFieldHelper(translate, credentials.password, passwordDirty);

    setPasswordHelper(helper);
    setIsPasswordInvalid(helperInErrorState(helper));
  }, [credentials.password, passwordDirty]);

  const isInvalid = () => {
    return Object.values(credentials).some((field) => !field);
  };

  const renderErrorNotice = () => {
    const getErroredFieldButton = (fieldId: string) => (
      <ListItem key={`erroredField-${fieldId}-button`}>
        <Button
          text={translate(`errored.field.button.${fieldId}`)}
          type="withdrawal"
          link
          onClick={() => {
            document.querySelectorAll(`#${fieldId}`).forEach((element) => {
              // @ts-ignore
              element.offsetParent !== null && element.focus();
            });
          }}
        />
      </ListItem>
    );

    if (showErrorNotice) {
      return (
        <>
          <Notice type="error">
            <div role="alert">
              {translate('form.error')}
              {[isUsernameInvalid, isPasswordInvalid].some((fieldInvalid) => fieldInvalid === true) && (
                <>
                  <Separator spacing="xxsmall" color="invisible" />
                  <List type="unstyled">
                    {isUsernameInvalid && getErroredFieldButton(USERNAME_FIELD_ID)}
                    {isPasswordInvalid && getErroredFieldButton(PW_FIELD_ID)}
                  </List>
                </>
              )}
            </div>
          </Notice>
          <Separator spacing="xsmall" color="invisible" />
        </>
      );
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    runInTeliaRumService('Autentimine (teenindaja)', () => {
      event.preventDefault();
      setUsernameDirty(true);
      setPasswordDirty(true);
      if (!isInvalid()) {
        handleSubmit();
      } else {
        setShowErrorNotice(true);
      }
    });
  };

  return (
    <Text>
      <form onSubmit={handleFormSubmit} id="dealerLogin">
        {renderErrorNotice()}
        <Grid align={['center-sm']}>
          <GridColumn width={getLoginPanelGridWidth()}>
            <Text>
              <TextField
                label={translate('panel.admin.username')}
                id={USERNAME_FIELD_ID}
                value={credentials.username}
                onChange={(value) => (credentials.username = value)}
                isInvalid={isUsernameInvalid}
                input={{ autoCapitalize: 'none', autoComplete: 'username' }}
                errorMessage={getHelperErrorMessage(usernameHelper)}
                required={true}
              />
              <PassField
                label={translate('panel.admin.password')}
                id={PW_FIELD_ID}
                value={credentials.password}
                onChange={(value) => (credentials.password = value)}
                isInvalid={isPasswordInvalid}
                errorMessage={getHelperErrorMessage(passwordHelper)}
                required={true}
                input={{ autoComplete: 'current-password' }}
                employee={true}
              />
              <LoginButton isLoading={isSubmitting} disabled={isSubmitting} type="employee" onClick={() => setSelectedTab('admin')} icon="#support" />
            </Text>
          </GridColumn>
        </Grid>
      </form>
    </Text>
  );
});

export default DealerLoginForm;
